import { useEffect } from 'react';

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormField,
  IconAddCandidateOutline,
  IconChevronRight,
  SlideIn,
} from '@/components';
import { CircularIconContainer } from '@/components/Icons/IconContainer/';
import {
  DepartmentType,
  RoleType,
  type UserBasicDto,
  UserPositionType,
  type UserRequest,
} from '@/services/api';
import { splitNameAndSurname } from '@/utils/identity';

import { useCompanyUserAddMutation } from './queries';
import { useEditCompanyUserMutation } from './queries';
import { zFormFieldsCompanyUser } from './schemas';

interface AddCompanyUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit?: boolean;
  user?: UserBasicDto;
}

type FormInputs = {
  name: string;
  surname: string;
  email: string;
  role: RoleType;
  department?: DepartmentType;
};

export const AddOrEditCompanyUserModal: React.FC<AddCompanyUserModalProps> = ({
  isOpen,
  onClose,
  isEdit = false,
  user,
}) => {
  const toast = useToast();
  const { t } = useTranslation(['common', 'components', 'auth']);

  const editCompanyUserMutation = useEditCompanyUserMutation({
    async onSuccess() {
      toast({
        title: t('components:settings.account.api.editCompanyUserSuccess'),
        status: 'success',
      });
      onClose();
    },
    onError() {
      toast({
        title: t('components:settings.account.api.editCompanyUserError'),
        status: 'error',
      });
    },
  });

  const companyUserAddMutation = useCompanyUserAddMutation({
    async onSuccess() {
      toast({
        title: t('components:settings.account.api.newCompanyUserAdded'),
        status: 'success',
      });

      form.reset();
      onClose();
    },
    onError() {
      toast({
        title: t('components:settings.account.api.addCompanyUserError'),
        status: 'error',
      });
    },
  });

  const form = useForm<FormInputs>({
    mode: 'onBlur',
    resolver: zodResolver(zFormFieldsCompanyUser()),
    defaultValues: {
      name: '',
      email: '',
      role: undefined,
      department: undefined,
    },
  });

  // Reset form when user prop changes (for edit mode)
  useEffect(() => {
    if (isOpen) {
      if (user && isEdit) {
        form.reset({
          name: user.name + ' ' + user.surname,
          email: user.email,
          role: user.role,
          department: user.department,
        });
      } else {
        form.reset({
          name: '',
          email: '',
          role: undefined,
          department: undefined,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    user?.id,
    user?.name,
    user?.surname,
    user?.email,
    user?.role,
    user?.department,
    isEdit,
    form,
  ]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const inviteCompanyUserRequest: UserRequest = {
      name: splitNameAndSurname(data.name!).firstName,
      surname: splitNameAndSurname(data.name!).surname,
      email: data.email?.trim(),
      role: data.role,
      department: data.department,
    };

    if (isEdit) {
      await editCompanyUserMutation.mutateAsync({
        id: user?.id,
        ...inviteCompanyUserRequest,
      });
      return;
    }
    await companyUserAddMutation.mutateAsync(inviteCompanyUserRequest);
    form.setValue('email', '');
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={() => {
        form.reset();
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader justifyItems="center">
          <VStack>
            <CircularIconContainer icon={IconAddCandidateOutline} />
            {isEdit ? (
              <Text>{t('components:settings.account.modal.edit.title')}</Text>
            ) : (
              <Text>{t('common:actions.inviteNewMember')}</Text>
            )}
          </VStack>
        </ModalHeader>
        <ModalBody w="full" pb={2}>
          <SlideIn>
            <Form {...form}>
              <Stack spacing={4}>
                <HStack align="start">
                  <FormField
                    placeholder={t('common:information.fullName')}
                    type="text"
                    control={form.control}
                    name="name"
                    size="lg"
                  />
                </HStack>
                <FormField
                  placeholder={t('common:information.email')}
                  type="email"
                  control={form.control}
                  name="email"
                  size="lg"
                />

                <FormField
                  type="select"
                  name="role"
                  placeholder={t('common:actions.selectRole')}
                  options={ROLES}
                  rules={{
                    required: t('auth:users.data.role.required'),
                  }}
                  control={form.control}
                />

                <FormField
                  type="select"
                  name="department"
                  control={form.control}
                  placeholder={t('common:actions.selectDepartment')}
                  options={DEPARTMENTS}
                />

                {!isEdit ? (
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      const values = form.getValues();
                      onSubmit(values);
                    }}
                    mb={8}
                    mt={4}
                    isLoading={companyUserAddMutation.isPending}
                    rightIcon={<IconChevronRight fontSize="20px" />}
                    variant="@primary"
                    w="full"
                  >
                    {t('common:actions.invite')}
                  </Button>
                ) : (
                  <HStack>
                    <Button
                      mb={8}
                      mt={4}
                      variant="outline"
                      w="full"
                      onClick={onClose}
                    >
                      {t('common:actions.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      mb={8}
                      mt={4}
                      isLoading={editCompanyUserMutation.isPending}
                      variant="@primary"
                      w="full"
                      onClick={(e) => {
                        e.preventDefault();
                        const values = form.getValues();
                        onSubmit(values);
                      }}
                    >
                      {t('common:actions.update')}
                    </Button>
                  </HStack>
                )}
              </Stack>
            </Form>
          </SlideIn>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const ROLES = [
  {
    label: t('common:information.roles.SuperAdmin'),
    value: RoleType.SuperAdmin,
  },
  {
    label: t('common:information.roles.Expert'),
    value: RoleType.Expert,
  },
  {
    label: t('common:information.roles.Administrator'),
    value: RoleType.Administrator,
  },
];

export const POSITIONS = [
  {
    label: t('common:information.positions.GeneralManager'),
    value: UserPositionType.GeneralManager,
  },
  {
    label: t('common:information.positions.Product'),
    value: UserPositionType.Product,
  },
  {
    label: t('common:information.positions.HRManager'),
    value: UserPositionType.HRManager,
  },
];

export const DEPARTMENTS = [
  {
    label: t('components:board.humanResources'),
    value: DepartmentType.HumanResources,
  },
  {
    label: t('components:board.marketing'),
    value: DepartmentType.Marketing,
  },
  {
    label: t('components:board.sales'),
    value: DepartmentType.Sales,
  },
  {
    label: t('components:board.informationTechnologies'),
    value: DepartmentType.InformationTechnologies,
  },
  {
    label: t('components:board.finance'),
    value: DepartmentType.Finance,
  },
];
